import React, { FC } from 'react'

import { ArticlePublication } from 'common/client-data-types/publication'
import {
    InlineContentTypes,
    InlineAdContent,
} from 'component-library/templates/Publication/SharedPublication.routing'
import { AdState } from 'common/advertising/ad-state'
import { InlinePositionedContent } from 'component-library/templates/Publication/Publication.props'
import { createRenderTarget } from 'component-library/render-target'
import {
    StyledAmpSpace,
    StyledAmpAd,
    StyledAmpAdWrapper,
} from 'component-library/google-amp/amp.styled'
import { AmpBigMobileAd } from 'component-library/google-amp/components/AmpBigMobileAd'
import { AmpAd } from 'component-library/google-amp/components/AmpAd'
import { AdDefinition } from 'common/advertising/add-ads'
import { AdEvent } from 'common/events/viewability-event'
import { FeatureToggle } from 'component-library/feature-toggling/Toggle/Toggle'
import {
    AdUnitWrapper,
    AdUnitWrapperProps,
} from 'component-library/advertising/AdUnit/AdUnitWrapper'

export interface CommonProps {
    unit: AdUnitWrapperProps & {
        slot: AdDefinition
    }
    ads: AdState
    position: number
    adEvent: (event: AdEvent) => void
}

const WebAdUnit: FC<CommonProps> = ({ unit, ads, adEvent }) => (
    <AdUnitWrapper
        {...unit}
        key={unit.slot.id}
        adState={ads}
        unitId={unit.slot.id}
        onEvent={adEvent}
    />
)

const AmpAdUnit: FC<CommonProps> = ({ unit, ads, position }) => (
    <StyledAmpAdWrapper>
        <StyledAmpSpace />
        <StyledAmpAd id={`inlineAdContent[${position}]`}>
            <FeatureToggle
                feature="big-mobile-amp"
                on={() => (
                    <AmpBigMobileAd adState={ads} unitId={unit.slot.id} layout="responsive" />
                )}
                off={() => <AmpAd adState={ads} unitId={unit.slot.id} />}
            />
        </StyledAmpAd>
    </StyledAmpAdWrapper>
)

const AdUnit = createRenderTarget('adUnit', {
    web: WebAdUnit,
    amp: AmpAdUnit,
    preview: WebAdUnit,
    rss: WebAdUnit,
})

const mapAdDefinitionToInlineElement = (
    adContent: InlineAdContent,
    ads: AdState,
    publication: ArticlePublication,
    adEvent: (event: AdEvent) => void
): InlinePositionedContent | undefined => {
    const insertAfter = adContent.insertAfter ? adContent.insertAfter : 4

    const firstContentBlocks = publication.content.slice(0, insertAfter)
    let alignedImagePosition: number | undefined

    firstContentBlocks.some((block, index) => {
        if (block.kind === 'inline' && block.inlineKind === 'image' && block.align !== undefined) {
            alignedImagePosition = index
            return true
        }
        return false
    })

    if (alignedImagePosition && alignedImagePosition < insertAfter) {
        return undefined
    }

    const position = alignedImagePosition === insertAfter ? insertAfter - 1 : insertAfter

    return {
        element: (
            <React.Fragment>
                {adContent.content.map(unit => (
                    <AdUnit
                        key={unit.slot.id}
                        unit={unit}
                        ads={ads}
                        position={position}
                        adEvent={adEvent}
                    />
                ))}
            </React.Fragment>
        ),
        position,
    }
}

export function isInlineAdContent(content: InlineContentTypes): content is InlineAdContent {
    return content.kind === 'ad'
}

// TODO not sure if this is correct but the previous product version had an extra data field which seemed to be duped.
interface GetInlinePositionedContentProps {
    adState: AdState
    publication: ArticlePublication
    inlinePublicationContent: InlineContentTypes[]
    onEvent: (event: AdEvent) => void
}

export const getInlinePositionedAds = (
    props: GetInlinePositionedContentProps
): InlinePositionedContent[] => {
    const { adState, publication, inlinePublicationContent, onEvent } = props

    if (publication.kind !== 'article') {
        return []
    }

    /** Don't insert ads if there are less than 8 content blocks in the story */
    if (publication.content.length < 8) {
        return []
    }

    return inlinePublicationContent
        .filter(isInlineAdContent)
        .reduce((acc: InlinePositionedContent[], content: InlineAdContent) => {
            const mappedAdContent = mapAdDefinitionToInlineElement(
                content,
                adState,
                publication,
                onEvent
            )
            if (mappedAdContent) {
                acc.push(mappedAdContent)
            }
            return acc
        }, [])
}
